import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { 
  ArrowLeft, Gift, Star, Trophy, Coins, TrendingUp, 
  Clock, Sparkles, Camera, Loader2, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const InsulRewardsPage = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [points, setPoints] = useState({ total_points: 0, lifetime_points: 0, points_redeemed: 0 });
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    fetchPoints();
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPoints = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/rewards/points`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPoints(response.data);
    } catch (error) {
      toast.error('Failed to load reward points');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/rewards/history?limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(response.data.transactions || []);
    } catch (error) {
      console.error('Failed to load history:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Future rewards that can be redeemed
  const availableRewards = [
    { id: 1, name: 'Skip Ads (1 Day)', points: 50, icon: Sparkles, description: 'Skip all micronutrient ads for 24 hours', available: false },
    { id: 2, name: 'Premium Feature Access', points: 200, icon: Trophy, description: 'Unlock premium features for 1 week', available: false },
    { id: 3, name: 'Priority AI Analysis', points: 100, icon: TrendingUp, description: 'Get faster AI meal analysis', available: false },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-yellow-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-yellow-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-lg border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/dashboard')}
                className="rounded-full"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center">
                  <Gift className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold bg-gradient-to-r from-amber-600 to-yellow-600 bg-clip-text text-transparent">
                    InsulRewards
                  </h1>
                  <p className="text-xs text-muted-foreground">Earn points, unlock rewards</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Points Balance Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-amber-500 to-yellow-500 rounded-3xl p-8 text-white mb-8 shadow-xl"
        >
          <div className="flex items-center gap-3 mb-4">
            <Coins className="w-8 h-8" />
            <span className="text-lg font-medium opacity-90">Your Points Balance</span>
          </div>
          
          <div className="flex items-end gap-2 mb-6">
            <span className="text-6xl font-bold" data-testid="total-points">{points.total_points}</span>
            <span className="text-2xl opacity-80 mb-2">points</span>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white/20 rounded-xl p-4">
              <p className="text-sm opacity-80">Lifetime Earned</p>
              <p className="text-2xl font-bold">{points.lifetime_points}</p>
            </div>
            <div className="bg-white/20 rounded-xl p-4">
              <p className="text-sm opacity-80">Points Redeemed</p>
              <p className="text-2xl font-bold">{points.points_redeemed}</p>
            </div>
          </div>
        </motion.div>

        {/* How to Earn Points */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-3xl shadow-lg p-6 mb-8"
        >
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Star className="w-5 h-5 text-amber-500" />
            How to Earn Points
          </h2>
          
          <div className="space-y-3">
            <div className="flex items-center gap-4 p-4 bg-amber-50 rounded-xl border border-amber-200">
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <Camera className="w-6 h-6 text-amber-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold">Upload a Meal or Drink</h3>
                <p className="text-sm text-muted-foreground">Earn 1 point for every food entry you upload</p>
              </div>
              <div className="text-right">
                <span className="text-2xl font-bold text-amber-600">+1</span>
                <p className="text-xs text-muted-foreground">point</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl border border-gray-200 opacity-60">
              <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                <Trophy className="w-6 h-6 text-gray-400" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-500">Weekly Streak Bonus</h3>
                <p className="text-sm text-muted-foreground">Coming soon...</p>
              </div>
              <div className="text-right">
                <span className="text-sm text-gray-400">Coming Soon</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Available Rewards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-3xl shadow-lg p-6 mb-8"
        >
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Gift className="w-5 h-5 text-amber-500" />
            Rewards Catalog
            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full ml-2">Coming Soon</span>
          </h2>
          
          <div className="space-y-3">
            {availableRewards.map((reward) => (
              <div 
                key={reward.id}
                className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl border border-gray-200 opacity-60"
              >
                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                  <reward.icon className="w-6 h-6 text-gray-400" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-600">{reward.name}</h3>
                  <p className="text-sm text-muted-foreground">{reward.description}</p>
                </div>
                <div className="text-right">
                  <div className="flex items-center gap-1 text-gray-500">
                    <Coins className="w-4 h-4" />
                    <span className="font-bold">{reward.points}</span>
                  </div>
                  <Button disabled size="sm" className="mt-2 rounded-full opacity-50">
                    Redeem
                  </Button>
                </div>
              </div>
            ))}
          </div>
          
          <p className="text-center text-sm text-muted-foreground mt-4">
            Keep earning points! Redemption features coming soon.
          </p>
        </motion.div>

        {/* Points History */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-3xl shadow-lg p-6"
        >
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-amber-500" />
            Recent Activity
          </h2>
          
          {historyLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Camera className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No activity yet. Upload a meal to earn your first point!</p>
              <Button 
                onClick={() => navigate('/dashboard')} 
                className="mt-4 rounded-full bg-gradient-to-r from-amber-500 to-yellow-500"
              >
                Upload a Meal
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {history.map((transaction) => (
                <div 
                  key={transaction.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-xl"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      transaction.transaction_type === 'earned' 
                        ? 'bg-green-100 text-green-600' 
                        : 'bg-red-100 text-red-600'
                    }`}>
                      {transaction.transaction_type === 'earned' ? '+' : '-'}
                    </div>
                    <div>
                      <p className="font-medium text-sm">{transaction.description}</p>
                      <p className="text-xs text-muted-foreground">{formatDate(transaction.created_at)}</p>
                    </div>
                  </div>
                  <span className={`font-bold ${
                    transaction.points > 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {transaction.points > 0 ? '+' : ''}{transaction.points}
                  </span>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </main>
    </div>
  );
};

export default InsulRewardsPage;
